import type { LiveSessionSessionType, SessionCategory } from '../utils/scheduledSessionStorage';
import {
  categoryToLiveSessionType,
  isWorkshopCategory,
} from '../utils/sessionCategoryUtils';

export type OptionalSessionPurpose = 'tracking' | 'remedial' | 'counselling';

export interface LiveSessionCategoryMeta {
  id: SessionCategory;
  label: string;
  shortLabel: string;
  codePrefix: string;
  description: string;
  durationHint: string;
  defaultDurationMinutes: number;
}

export const LIVE_CLASS_CATEGORY: LiveSessionCategoryMeta = {
  id: 'mandatory',
  label: 'Live Class Mandatory',
  shortLabel: 'Live Class Mandatory',
  codePrefix: 'LCM',
  description: 'Scheduled curriculum session with topic plan, quizzes, and live instruction.',
  durationHint: 'Typically 1–2 hours',
  defaultDurationMinutes: 90,
};

export const WORKSHOP_ONLINE_CATEGORY: LiveSessionCategoryMeta = {
  id: 'workshop_online',
  label: 'Online Workshop',
  shortLabel: 'Online Workshop',
  codePrefix: 'LW-Online',
  description: 'Remote full-day workshop with live streaming and digital day-wise materials.',
  durationHint: 'Full day per workshop day',
  defaultDurationMinutes: 480,
};

export const WORKSHOP_ONSITE_CATEGORY: LiveSessionCategoryMeta = {
  id: 'workshop_onsite',
  label: 'Onsite Workshop',
  shortLabel: 'Onsite Workshop',
  codePrefix: 'LW-Onsite',
  description: 'In-person full-day workshop on campus with day-wise learning modules.',
  durationHint: 'Full day per workshop day',
  defaultDurationMinutes: 480,
};

export const SUPPORT_SESSION_CATEGORY: LiveSessionCategoryMeta = {
  id: 'optional',
  label: 'Live Class Optional',
  shortLabel: 'Live Class Optional',
  codePrefix: 'LCO',
  description: 'Short optional sessions for progress tracking, remedial help, or counselling.',
  durationHint: 'Typically 10–30 minutes',
  defaultDurationMinutes: 20,
};

export const LIVE_SESSION_CATEGORIES: Record<SessionCategory, LiveSessionCategoryMeta> = {
  mandatory: LIVE_CLASS_CATEGORY,
  workshop_online: WORKSHOP_ONLINE_CATEGORY,
  workshop_onsite: WORKSHOP_ONSITE_CATEGORY,
  optional: SUPPORT_SESSION_CATEGORY,
};

export const LIVE_SESSION_CATEGORY_OPTIONS = Object.values(LIVE_SESSION_CATEGORIES).map(
  (category) => ({
    value: category.id,
    label: `${category.label} (${category.codePrefix})`,
    description: `${category.durationHint}. ${category.description}`,
  }),
);

export const OPTIONAL_SESSION_PURPOSES: Record<
  OptionalSessionPurpose,
  { label: string; description: string; liveSessionType: LiveSessionSessionType }
> = {
  tracking: {
    label: 'Tracking Session',
    description: 'Quick progress check-in (10–30 min).',
    liveSessionType: 'tracking',
  },
  remedial: {
    label: 'Remedial Session',
    description: 'Targeted support for students who need extra help.',
    liveSessionType: 'online',
  },
  counselling: {
    label: 'Counselling Session',
    description: 'One-to-one or small-group guidance based on feedback.',
    liveSessionType: 'online',
  },
};

export function getLiveSessionCategoryMeta(
  category: SessionCategory | undefined,
): LiveSessionCategoryMeta {
  return LIVE_SESSION_CATEGORIES[category ?? 'mandatory'];
}

export function getSessionDisplayLabel(
  category: SessionCategory,
  liveSessionType?: LiveSessionSessionType,
): string {
  if (isWorkshopCategory(category)) {
    return getLiveSessionCategoryMeta(category).label;
  }
  if (category === 'optional' && liveSessionType === 'tracking') {
    return OPTIONAL_SESSION_PURPOSES.tracking.label;
  }
  return getLiveSessionCategoryMeta(category).label;
}

export function getSessionCodePrefix(category: SessionCategory): string {
  return getLiveSessionCategoryMeta(category).codePrefix;
}

export const LIVE_SESSION_NAMING_GUIDE =
  'LCM = Live Class Mandatory · LCO = Live Class Optional · LW-Online = Online Workshop · LW-Onsite = Onsite Workshop';

export { categoryToLiveSessionType, isWorkshopCategory };
